import {
  useQuery,
  useMutation,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  getApiSettingsGetAppointmentSettings,
  getApiPatientPortalAppointmentHistorical,
  getApiPatientPortalAppointmentUpcoming,
  postApiPatientPortalMultiMediaRequestMultiMedia,
  patchApiPatientPortalChartsUpdate,
  getApiPatientPortalMultiMediaGetMultiMediaById,
  getApiPatientPortalBillingGetPaymentDetails,
  postApiPatientPortalBillingGenerateStatementForPatientPayment,
  getApiPatientPortalBillingGetPatientStatements,
  getApiBillingGetPatientStatementEncounterSnapshotsPatientStatementDeliveryLogId,
  postApiPatientPortalMultiMediaGetMultimedia,
  postApiPatientPortalMultiMediaUploadMultimedia,
  getApiPatientPortalOrganizationGetOrg,
  getApiPatientPortalOrganizationGetOrgServiceFacilities,
  postApiPatientPortalAppointmentUpdateStatus,
  getApiPatientPortalChartsGet,
  postApiPatientPortalAuthSendOTP,
  postApiPatientPortalAuthVerifyOTP,
  postApiPatientPortalAuthGetToken,
  postApiPatientPortalAuthRefreshToken,
  postApiPatientPortalAuthSendMagicLink,
  postApiPatientPortalAuthCheckPasswordlessStatus,
  postApiPatientPortalAuthApproveLogin,
  postApiPatientPortalAuthLogout,
  getApiPatientPortalAuthGetLoginOptions,
  getApiPatientPortalModMedUATGetModMedOrganizations,
  postApiPatientPortalModMedUATGetAppointmentsForPatientByDate,
  postApiPatientPortalModMedUATCallForAppointmentReminder,
  getApiPatientPortalProviderSearchExternalProvider,
  getApiRTMGetStatusByPatientId,
  getApiPatientGetPCCItemsByPatientIdPatientId,
  getApiFormWorkflowGetRenderingMetadataFormWorkflowIdAssignee,
  getApiFormWorkflowGetWorkflowPdfsFormWorkflowId,
  postApiFormWorkflowCompletePatientStep,
} from '@/endpoints';
import {
  AppointmentConfirmationRequestDTO,
  ApproveLoginResponseDTO,
  CheckPasswordlessStatusDTO,
  DeliveredPatientStatementDTO,
  FormFieldAssignee,
  FormsRenderingMetadataResponseDTO,
  FormWorkflowCompletionRequestDTO,
  FormWorkflowWithTransitionSuccessMessageResponseDTO,
  GetTokenRequestDTO,
  GetTokenResponseDTO,
  ModifyAppointmentStatusResponseDTO,
  ModMedOrganizationDTO,
  ModMedUATAppointmentDTO,
  ModMedUATAppointmentsRequestDTO,
  ModMedUATSendCallRequestDTO,
  ModMedUATSendCallResponseDTO,
  MultiMediaRequestDTO,
  MultiMediaResponseDTO,
  OrganizationDTO,
  PatientDTO,
  PatientLoginRequestDTO,
  PatientMultiMediaResponseDTO,
  PatientPaymentDetailsDTO,
  PatientPortalLoginOptionsDTO,
  PatientPortalMultimediaRequestDTO,
  PatientPortalMultiMediaUploadDTO,
  PatientStatementAppointmentSnapshotDTO,
  PatientStatementForPaymentResponseDTO,
  PatientVisitDTO,
  PostApiPatientPortalAuthApproveLoginParams,
  PostApiPatientPortalAuthLogoutParams,
  RefreshTokenRequestDTO,
  SendMagicLinkRequestDTO,
  SendMagicLinkResponseDTO,
  SendOTPResponseDTO,
  ServiceFacilityDTOListDTO,
  SettingsDTO,
  NPISearchItemDTO,
  GetApiPatientPortalProviderSearchExternalProviderParams,
  VerifyOTPRequestDTO,
  VerifyOTPResponseDTO,
  RTMPatientStatusResponseDTO,
  PatientPCCItemsDTO,
} from '@/dtos';
import {
  isEmptyString,
} from '@/utils/misc';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import {
  normalizePatient,
} from '@/utils/helper';
import {
  MediaOriginatorType,
  MultimediaType,
} from '@/components/utils/constants';

export function useSendOTPForLogin(): UseMutationResult<SendOTPResponseDTO, Error, PatientLoginRequestDTO> {
  return useMutation<SendOTPResponseDTO, Error, PatientLoginRequestDTO>({
    mutationFn: (data) => postApiPatientPortalAuthSendOTP(data),
  });
}

export function useExchangeOtpForToken(): UseMutationResult<VerifyOTPResponseDTO, Error, VerifyOTPRequestDTO> {
  return useMutation<VerifyOTPResponseDTO, Error, VerifyOTPRequestDTO>({
    mutationFn: (data) => postApiPatientPortalAuthVerifyOTP(data),
  });
}

export function useGetToken(): UseMutationResult<GetTokenResponseDTO, Error, GetTokenRequestDTO> {
  return useMutation<GetTokenResponseDTO, Error, GetTokenRequestDTO>({
    mutationFn: (data) => postApiPatientPortalAuthGetToken(data),
  });
}

export function useRefreshToken(): UseMutationResult<GetTokenResponseDTO, Error, RefreshTokenRequestDTO> {
  return useMutation<GetTokenResponseDTO, Error, RefreshTokenRequestDTO>({
    mutationFn: (data) => postApiPatientPortalAuthRefreshToken(data),
  });
}

export function useSendMagicLink(): UseMutationResult<SendMagicLinkResponseDTO, Error, SendMagicLinkRequestDTO> {
  return useMutation<SendMagicLinkResponseDTO, Error, SendMagicLinkRequestDTO>({
    mutationFn: (data) => postApiPatientPortalAuthSendMagicLink(data),
  });
}

export function useCheckPasswordlessStatus(): UseMutationResult<ApproveLoginResponseDTO, Error, CheckPasswordlessStatusDTO> {
  return useMutation<ApproveLoginResponseDTO, Error, CheckPasswordlessStatusDTO>({
    mutationFn: (data) => postApiPatientPortalAuthCheckPasswordlessStatus(data),
  });
}

export function useApproveLogin(): UseMutationResult<ApproveLoginResponseDTO, Error, PostApiPatientPortalAuthApproveLoginParams> {
  return useMutation<ApproveLoginResponseDTO, Error, PostApiPatientPortalAuthApproveLoginParams>({
    mutationFn: (data) => postApiPatientPortalAuthApproveLogin(data),
  });
}

export function useAuthLogout(): UseMutationResult<GetTokenResponseDTO, Error, PostApiPatientPortalAuthLogoutParams> {
  return useMutation<GetTokenResponseDTO, Error, PostApiPatientPortalAuthLogoutParams>({
    mutationFn: (data) => postApiPatientPortalAuthLogout(data),
  });
}

export function useGetPatient(enabled: boolean = false) {
  return useQuery<PatientDTO, Error, PatientDTO>({
    queryKey: ['patient'],
    queryFn: async() => {
      const patient = await getApiPatientPortalChartsGet();
      return normalizePatient(patient);
    },
    enabled,
  });
}

export function useGetPastVisits(enabled: boolean = true) {
  return useQuery<PatientVisitDTO[], Error, PatientVisitDTO[]>({
    queryKey: ['past-visits'],
    queryFn: () => getApiPatientPortalAppointmentHistorical(),
    enabled,
  });
}

export function useGetUpcomingVisits(enabled: boolean = true) {
  return useQuery<PatientVisitDTO[], Error, PatientVisitDTO[]>({
    queryKey: ['upcoming-visits'],
    queryFn: () => getApiPatientPortalAppointmentUpcoming(),
    enabled,
  });
}

export function useGetPatientProfilePicture(enabled: boolean = false) {
  // arg should be used to enable/disable the query
  return useQuery<string, Error, string>({
    queryKey: ['patient-multi-media'],
    queryFn: async () => {
      const response = await postApiPatientPortalMultiMediaRequestMultiMedia({
        generateUrls: true,
        list: [
          {
            originatorType: MediaOriginatorType.Patient,
            multimediaType: MultimediaType.ProfilePicture,
          },
        ],
        pageProperties: {
          page: 0,
          pageSize: 100,
        },
      });
      return head(response?.list)?.url?.value ?? '';
    },
  });
}

export function useUpdatePatientChart():
  UseMutationResult<PatientDTO, Error, Partial<PatientDTO>> {
  return useMutation<PatientDTO, Error, Partial<PatientDTO>>({
    mutationFn: async (data: Partial<PatientDTO>) => {
      return patchApiPatientPortalChartsUpdate(data);
    },
  });
}

export function useRequestMultiMedia(params?: MultiMediaRequestDTO, enabled: boolean = true) {
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['multiMedia', params],
    queryFn: () => postApiPatientPortalMultiMediaRequestMultiMedia(params ?? {}),
    enabled,
  });
}

export function useGetPatientMultimediaByMultiMediaId(): UseMutationResult<MultiMediaResponseDTO, Error, number> {
  return useMutation<MultiMediaResponseDTO, Error, number>({
    mutationFn: (patientMultiMediaId: number) => getApiPatientPortalMultiMediaGetMultiMediaById({ patientMultiMediaId }),
  });
}

export function usePortalGetPaymentDetails() {
  return useQuery<PatientPaymentDetailsDTO, Error>({
    queryKey: ['billing-summary-data'],
    queryFn: getApiPatientPortalBillingGetPaymentDetails,
  });
}

export function useGenerateStatementForPatientPayment(uniqueId: string | null) {
  return useQuery<PatientStatementForPaymentResponseDTO, Error>({
    queryKey: ['generate-statement-for-patient-payment', uniqueId],
    queryFn: async () => {
      const response = await postApiPatientPortalBillingGenerateStatementForPatientPayment();
      return response;
    },
    enabled: !isNil(uniqueId),
  });
}

export function useGetDeliveredPatientStatementsByPatientHash(hash: string) {
  return useQuery<DeliveredPatientStatementDTO[], Error>({
    queryKey: ['delivered-patient-statement', hash],
    queryFn: getApiPatientPortalBillingGetPatientStatements,
    enabled: !isEmptyString(hash),
    gcTime: 5 * 1000, // 5 seconds
  });
}

export function useGetMultiMedia(data: PatientPortalMultimediaRequestDTO, enabled = false) {
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['patient-multimedia', enabled],
    queryFn: () => postApiPatientPortalMultiMediaGetMultimedia(data),
    enabled,
  });
}

export function usePostPatientMultiMedia(): UseMutationResult<PatientMultiMediaResponseDTO, Error, PatientPortalMultiMediaUploadDTO> {
  return useMutation<PatientMultiMediaResponseDTO, Error, PatientPortalMultiMediaUploadDTO>({
    mutationFn: (data) => postApiPatientPortalMultiMediaUploadMultimedia(data),
  });
}

export function useGetOrgByPatientId(enabled: boolean = false) {
  return useQuery<OrganizationDTO, Error>({
    queryKey: ['patient-org', enabled],
    queryFn: getApiPatientPortalOrganizationGetOrg,
    enabled,
  });
}

export function useGetPatientOrgServiceFacilities() {
  return useQuery<ServiceFacilityDTOListDTO, Error>({
    queryKey: ['patient-service-facilities'],
    queryFn: () => getApiPatientPortalOrganizationGetOrgServiceFacilities(),
  });
}

export function useSearchExternalProviders(
  queryParams: GetApiPatientPortalProviderSearchExternalProviderParams,
  enabled: boolean,
) {
  return useQuery<NPISearchItemDTO[], Error>({
    queryKey: ['external-provider-search', queryParams],
    queryFn: async () => {
      const response = await getApiPatientPortalProviderSearchExternalProvider(queryParams);
      return response?.result ?? [];
    },
    enabled,
  });
}

export function useGetAppointmentSettings() {
  return useQuery<SettingsDTO, Error>({
    queryKey: ['appointment-settings'],
    queryFn: getApiSettingsGetAppointmentSettings,
  });
}

export function useSetAppointmentConfirmation(): UseMutationResult<ModifyAppointmentStatusResponseDTO, Error, AppointmentConfirmationRequestDTO> {
  return useMutation<ModifyAppointmentStatusResponseDTO, Error, AppointmentConfirmationRequestDTO>({
    mutationFn: (data) => postApiPatientPortalAppointmentUpdateStatus(data),
  });
}

export function useGetAuthLoginOptions() {
  return useQuery<PatientPortalLoginOptionsDTO, Error>({
    queryKey: ['auth-login-options'],
    queryFn: () => getApiPatientPortalAuthGetLoginOptions(),
  });
}

export function useGetModMedUATGetModMedOrganizations(enabled: boolean = true) {
  return useQuery<ModMedOrganizationDTO[], Error>({
    queryKey: ['modmed-uat-organizations'],
    queryFn: getApiPatientPortalModMedUATGetModMedOrganizations,
    enabled,
  });
}

export function useModMedUATCallForAppointmentReminder(): UseMutationResult<ModMedUATSendCallResponseDTO, Error, ModMedUATSendCallRequestDTO> {
  return useMutation<ModMedUATSendCallResponseDTO, Error, ModMedUATSendCallRequestDTO>({
    mutationFn: (data) => postApiPatientPortalModMedUATCallForAppointmentReminder(data),
  });
}

export function useModMedUATGetAppointmentsForPatientByDate():
  UseMutationResult<ModMedUATAppointmentDTO[], Error, ModMedUATAppointmentsRequestDTO> {
  return useMutation<ModMedUATAppointmentDTO[], Error, ModMedUATAppointmentsRequestDTO>({
    mutationFn: (data) => postApiPatientPortalModMedUATGetAppointmentsForPatientByDate(data),
  });
}

export function useGetPatientStatementEncounterSnapshots(
  patientStatementDeliveryLogId: number | undefined,
) {
  return useQuery<PatientStatementAppointmentSnapshotDTO[], Error>({
    queryKey: ['statement-encounter-snapshots', patientStatementDeliveryLogId],
    queryFn: () => getApiBillingGetPatientStatementEncounterSnapshotsPatientStatementDeliveryLogId(
      patientStatementDeliveryLogId!,
    ),
    enabled: !isNil(patientStatementDeliveryLogId),
  });
}

export function useGetRTMStatusByPatientId(patientId: number = 0) {
  return useQuery<RTMPatientStatusResponseDTO, Error>({
    queryKey: ['rtm-status', patientId],
    queryFn: async () => {
      const response = await getApiRTMGetStatusByPatientId({ patientId });
      return response;
    },
    enabled: patientId > 0,
    refetchOnMount: 'always',
  });
}

export function useGetPCCItemsByPatientId(patientId: number = 0) {
  return useQuery<PatientPCCItemsDTO, Error>({
    queryKey: ['pcc-items', patientId],
    queryFn: async () => {
      const response = await getApiPatientGetPCCItemsByPatientIdPatientId(patientId);
      return response;
    },
    enabled: patientId > 0,
    refetchOnMount: 'always',
  });
}

export function useFormWorkflowRenderingMetadata(
  formWorkflowId?: number,
  enabled: boolean = true,
): UseQueryResult<FormsRenderingMetadataResponseDTO, Error> {
  return useQuery<FormsRenderingMetadataResponseDTO, Error>({
    queryKey: ['formWorkflowRenderingMetadata', formWorkflowId],
    queryFn: () => getApiFormWorkflowGetRenderingMetadataFormWorkflowIdAssignee(
      formWorkflowId ?? 0,
      FormFieldAssignee.Patient,
    ),
    enabled: enabled && !isNil(formWorkflowId),
  });
}

export function useGetFormWorkflowPdfs(
  formWorkflowId?: number,
  enabled: boolean = true,
): UseQueryResult<MultiMediaResponseDTO, Error> {
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['formWorkflowPdfs', formWorkflowId],
    queryFn: () => getApiFormWorkflowGetWorkflowPdfsFormWorkflowId(formWorkflowId ?? 0),
    enabled: enabled && !isNil(formWorkflowId),
  });
}

export function useCompleteFormWorkflowPatientStep(): UseMutationResult<
  FormWorkflowWithTransitionSuccessMessageResponseDTO,
  Error,
  FormWorkflowCompletionRequestDTO
  > {
  return useMutation<
    FormWorkflowWithTransitionSuccessMessageResponseDTO,
    Error,
    FormWorkflowCompletionRequestDTO>({
      mutationFn: (payload) => postApiFormWorkflowCompletePatientStep(payload),
    });
}
